import React, {useEffect} from 'react'
import { useRequest } from 'ahooks'
import styled from 'styled-components'
import Header from 'plugin-smart-mobility-utils/shared/ListHeader/ListHeader'
import DynamicTable from 'plugin-smart-mobility-utils/shared/ui/Widget/DynamicTable/DynamicTable'
import InfoPill from '../InfoPill'
import { Celular1 } from '../Links/Cellular1'
import { Celular2 } from '../Links/Cellular2'
import { Satcom } from '../Links/Satcom'
import StatusIndicator from '../StatusIndicator'
import Subscriber from 'live/services/subscription'
import { fetchVehicles } from '../../api/vehicles-api'

import { Vehicle, VehicleTableRow } from '../../types'

type VehicleListRow = Vehicle & VehicleTableRow

type DynamicTableCellData = VehicleListRow


const getUplink = (vehicle: VehicleListRow, icon: string) => {
    const uplink = vehicle.uplinks.find((item) => item.icon === icon)

    return {
        icon,
        color: uplink?.color,
        background: uplink?.background
    }
}

const renderUplinks = (vehicle: VehicleListRow): JSX.Element => {
    const satcom = getUplink(vehicle, 'satcom')
    const celular1 = getUplink(vehicle, 'celular1')
    const celular2 = getUplink(vehicle, 'celular2')

    return (
        <UplinkList>
            <Satcom color={satcom.color} background={satcom.background} />
            <Celular1 color={celular1.color} background={celular1.background} />
            <Celular2 color={celular2.color} background={celular2.background} />
        </UplinkList>
    )
}

const getCameraStatus = (vehicle: VehicleListRow) => {
    const isOnline = vehicle.obc.status === 'Online'

    return {
        icon: isOnline ? 'fas fa-play' : 'fas fa-times',
        status: isOnline ? 'Active' : 'Inactive',
    }
}

const generateData = (vehicleListRows: Array<VehicleListRow>) => {
    return vehicleListRows.map((vehicle) => ({
        1: vehicle,
        2: vehicle,
        3: vehicle,
        4: vehicle,
        5: vehicle,
    }))
}




const navigateToCameraView = (eyerideVehicle: Vehicle): void => {
            window.history.pushState({ vehicle: eyerideVehicle }, null, `#/camera-view/${eyerideVehicle.vehicleId}`)
            window.history.go()
}

const TABLE_ROW_HEIGHT = 56



const createColumnsConfig = (
    renderLinkCell: (data: DynamicTableCellData, content: React.ReactNode) => JSX.Element
): Array<{column: number, headerTitle: string, component: ({ data }: { data: DynamicTableCellData }) => JSX.Element}> => [
    {column: 1, headerTitle: 'Vehicle ID', component: ({ data }) => renderLinkCell(data, data.vehicle_ID)},
    {
        column: 2,
        headerTitle: 'Camera Status',
        component: ({ data }) => {
            const cameraStatus = getCameraStatus(data)

            return renderLinkCell(
                data,
                <StatusIndicator
                    statusColor={data.obc.color}
                    status={cameraStatus.status}
                    statusIcon={cameraStatus.icon}
                    statusIconColor="white"
                />
            )
        },
    },
    {column: 3, headerTitle: 'Stack', component: ({ data }) => renderLinkCell(data, <InfoPill color={data.stack.color} info={data.stack.status} />)},
    {column: 4, headerTitle: 'Uplinks', component: ({ data }) => renderLinkCell(data, renderUplinks(data))},
    {
        column: 5,
        headerTitle: 'OBC Conectivity',
        component: ({ data }) => renderLinkCell(data, <StatusIndicator statusColor={data.obc.color} status={data.obc.status} />),
    },
]






const VehiclesList = (): JSX.Element => {
    const { data, loading, error } = useRequest(fetchVehicles)
    const [eventTable, setEventTable] = React.useState<VehicleTableRow[]>([])
    const [hoveredVehicleId, setHoveredVehicleId] = React.useState<string | null>(null)

    const renderLinkCell = (data: DynamicTableCellData, content: React.ReactNode): JSX.Element => (
        <CellLink
            href={`#/camera-view/${data.vehicleId}`}
            $isRowHovered={hoveredVehicleId === data.vehicleId}
            onMouseEnter={() => setHoveredVehicleId(data.vehicleId)}
            onMouseLeave={() => setHoveredVehicleId((current) => (current === data.vehicleId ? null : current))}
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault()
                navigateToCameraView(data)
            }}
        >
            {content}
        </CellLink>
    )

    const COLUMNS_CONFIG = createColumnsConfig(renderLinkCell)

      const subscriptionTable = new Subscriber({
        url: `/services/plugin-cameras/cameras/table`
})


      useEffect(() => {
        subscriptionTable.on('event', (events : any[]) => {
            setEventTable(events)
        })

        subscriptionTable.flush()

    }, [])

    useEffect(() => {console.log("Event table updated:", eventTable)}, [eventTable])

    const eventTableBySiteId = new Map(eventTable.map((item) => [item.site_id, item]))
    const mergedVehicles: VehicleListRow[] = ((data ?? []) as Vehicle[]).flatMap((vehicle) => {
    const tableRow = eventTableBySiteId.get(vehicle.LMOPsiteId)

        if (!tableRow) {
            return []
        }

        return [{
            ...vehicle,
            ...tableRow,
        }]
    })


    console.log("Merged Vehicles:", mergedVehicles)
    
    if (loading) {
        return <div>Loading vehicles...</div>
    }

    if (error) {
        return <div>Failed to load vehicles.</div>
    }

    return (
        <MainCameraContainer>
            <ContainerHeader>
                        <Header
                            title={'Live Cameras'}
                            icon={'far fa-video'}
                            total={data?.length || 0}
                            status={[]}
                        />
            </ContainerHeader>
                 <DynamicTable
                columns={COLUMNS_CONFIG}
                data={generateData(mergedVehicles)}
                styledTable={{
                    border: '1px solid var(--gray-300)',
                    borderRadius: '3px'
                }}
                styledRowBody={{
                    height: TABLE_ROW_HEIGHT,
                    borderBottom: '1px solid var(--gray-300) !important',
                    borderBottomColor: 'var(--gray-300) !important',
                    borderBottomWidth: '1px !important',
                    borderBottomStyle: 'solid !important',
                    color: 'var(--gray-800)',
                    fontFamily: "SourceSansPro-Semibold",
                    fontSize: "16px",
                    fontWeight: 600,
                    lineHeight: "16px",
                }}
                styledColumnHeader={{borderBottom: 'none', padding: '8px 16px', color: 'var(--gray-700)', fontSize: '16px', fontFamily: 'SourceSansPro-Semibold', fontWeight: 600, textTransform: 'uppercase'}}
                styledColumnBody={{padding: 0, height: TABLE_ROW_HEIGHT}}
                footer={false}
            />
        </MainCameraContainer>
    )
}


const MainCameraContainer = styled.div.attrs({
    className: 'main-camera-container',
})`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100vw;
    box-sizing: border-box;
    top: 35px;
    height: calc(100vh - 167px);
    padding: 0 24px;
    overflow: hide;

 
`

const ContainerHeader = styled.div`
    background-color: ${({ theme }) => theme['gray-0']};
`

const CellLink = styled.a<{ $isRowHovered: boolean }>`
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    width: 100%;
    height: ${TABLE_ROW_HEIGHT}px;
    padding: 8px 16px;
    box-sizing: border-box;

    &:hover {
        text-decoration: none;
        color: var(--gray-0);
    }

    ${({ $isRowHovered }) =>
        $isRowHovered
            ? `
        background-color: var(--blue-300);
        color: var(--blue-500);
    `
            : ''}

    &:visited {
        color: inherit;
    }

    &:focus-visible {
        outline: 2px solid var(--gray-500);
        outline-offset: 2px;
        border-radius: 2px;
    }
`

const UplinkList = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`

export default VehiclesList
